<script setup lang="ts">
import { computed, mergeProps, useAttrs } from 'vue';
import { omit } from 'lodash';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { useLayoutStore } from '@/stores/layoutStore.ts';
import { BaseImageProps } from './types';
import {
  generateSizes,
  generateSrcset,
  DESKTOP_IMAGE_WIDTH_OVERVIEW,
  MOBILE_IMAGE_WIDTH_OVERVIEW,
  getImageUrl,
} from './utils';
import { ensureUniqueClasses } from '@/utils/stringUtils';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<BaseImageProps>();
const attrs = useAttrs();

const layoutStore = useLayoutStore();

const imageBorder = computed(() =>
  props.hideImageFrame ? 'none' : '1px solid rgb(var(--v-theme-secondary-lighten-5))',
);

const classList = computed(() => {
  let imageClasses = attrs.class ? (attrs.class as string) : '';
  imageClasses = imageClasses.concat(' image');

  return ensureUniqueClasses(imageClasses);
});

// Determine responsive widths
const mobileWidth = computed(() => {
  const width = (() => {
    if (!props.width) return undefined;
    if (typeof props.width === 'string') return parseInt(props.width);
    return props.width;
  })();
  return props.mobileWidth ?? width ?? MOBILE_IMAGE_WIDTH_OVERVIEW;
});

const desktopWidth = computed(() => {
  const width = (() => {
    if (!props.width) return undefined;
    if (typeof props.width === 'string') return parseInt(props.width);
    return props.width;
  })();
  return props.desktopWidth ?? width ?? DESKTOP_IMAGE_WIDTH_OVERVIEW;
});

// Current effective width based on breakpoint
const currentWidth = computed(() => (layoutStore.mobile ? mobileWidth.value : desktopWidth.value));

// Calculate height if aspect ratio is provided
const currentHeight = computed(() => {
  if (props.aspectRatio) {
    const aspectRatio =
      typeof props.aspectRatio === 'string' ? parseFloat(props.aspectRatio) : props.aspectRatio;
    return Math.round(currentWidth.value / aspectRatio);
  }
  return typeof props.height === 'string' ? parseFloat(props.height) : props.height;
});

// Generate image path respecting cover prop for cropping behavior
const imagePath = computed(() => {
  if (!props.pictureId) return undefined;

  const { cover } = props;

  // Cover = true: request server-side cropped image when target box shape is specified
  if (cover) {
    if (props.height != null) {
      // Explicit height mode with cropping
      const height = typeof props.height === 'string' ? parseFloat(props.height) : props.height;
      return getImageUrl(props.pictureId, {
        width: currentWidth.value,
        height,
        cropMode: props.cropMode,
      });
    } else if (props.aspectRatio != null) {
      // Aspect ratio mode with cropping
      const aspectRatio =
        typeof props.aspectRatio === 'string' ? parseFloat(props.aspectRatio) : props.aspectRatio;
      return getImageUrl(props.pictureId, {
        width: currentWidth.value,
        aspectRatio,
        cropMode: props.cropMode,
      });
    }
    // Width-only mode (no cropping needed)
    return getImageUrl(props.pictureId, { width: currentWidth.value });
  }

  // Cover = false: never request cropped variants; always width-only to preserve full image
  return getImageUrl(props.pictureId, { width: currentWidth.value });
});

// Generate srcset if enabled
const srcset = computed(() => {
  if (!props.generateSrcset || !props.pictureId || props.srcset) {
    return props.srcset; // Use custom srcset if provided
  }

  // Respect cover prop: only pass aspectRatio when cover is true
  const aspectRatio = (() => {
    if (props.cover && props.aspectRatio) {
      return typeof props.aspectRatio === 'string'
        ? parseFloat(props.aspectRatio)
        : props.aspectRatio;
    }
    return undefined;
  })();

  return generateSrcset(props.pictureId, currentWidth.value, aspectRatio, props.cropMode);
});

// Generate sizes attribute
const sizes = computed(() => {
  if (props.sizes) {
    return props.sizes; // Use custom sizes if provided
  }
  return generateSizes(mobileWidth.value, desktopWidth.value);
});

// Determine aspect ratio for display
const displayAspectRatio = computed(() => (props.pictureId ? props.aspectRatio : 1));
</script>

<template>
  <Tooltip :disabled="!tooltip" position="bottom">
    <template #tooltip>{{ tooltip }}</template>
    <template #target="{ props: targetProps }">
      <VImg
        v-bind="
          mergeProps(
            targetProps,
            omit(props, [
              'fallbackIcon',
              'tooltip',
              'pictureId',
              'mobileWidth',
              'desktopWidth',
              'cropMode',
              'generateSrcset',
              'srcsetSizes',
              'hideImageFrame',
              'hideLoadingPlaceholder',
            ]),
            omit($attrs, 'class'),
            {
              class: classList,
            },
          )
        "
        :aspectRatio="displayAspectRatio"
        :cover="pictureId ? props.cover : true"
        :src="imagePath"
        :srcset="srcset"
        :sizes="sizes"
        :width="currentWidth"
        :height="props.height ? currentHeight : undefined"
        :data-testid="$attrs['data-testid']"
        maxWidth="100%"
        :alt="alt"
      >
        <template #placeholder>
          <!-- hideLoadingPlaceholder: stay transparent while the image loads
               (only VImg's fade shows), but still surface the fallback icon
               when there is genuinely no image to load. -->
          <span
            v-if="!hideLoadingPlaceholder || !pictureId"
            :class="{
              fallback: true,
              'border-half': rounded,
              'border-default': !rounded,
            }"
          >
            <v-icon class="icon" color="off-white">
              {{ fallbackIcon }}
            </v-icon>
          </span>
        </template>
        <template v-if="hideLoadingPlaceholder" #error>
          <!-- a failed load must not leave an empty hole -->
          <span
            :class="{
              fallback: true,
              'border-half': rounded,
              'border-default': !rounded,
            }"
          >
            <v-icon class="icon" color="off-white">
              {{ fallbackIcon }}
            </v-icon>
          </span>
        </template>
      </VImg>
    </template>
  </Tooltip>
</template>

<style scoped lang="scss">
@use '@/styles/variables';

.image {
  /* Let VImg handle object-fit via its cover prop (default: contain) */
  aspect-ratio: v-bind('displayAspectRatio');
  max-width: 100%;
  max-height: fit-content;
  border: v-bind(imageBorder);
}

.fallback {
  width: 100%;
  position: relative;
  background: rgb(var(--v-theme-secondary-lighten-2));
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: v-bind('displayAspectRatio');
}

.icon {
  width: 70%;
  height: auto;
}

.border-default {
  border-radius: variables.$border-radius-default;
}
.border-half {
  border-radius: 50%;
}
</style>
